import React from "react";
import { Link } from "react-router-dom";
import { BarChart3, TrendingUp } from "lucide-react";
import { Loading, Empty, SectionHeader, LoadError, usePlatformFetch } from "./_primitives";

export default function AdoptionAnalytics() {
  const { data, error, retry } = usePlatformFetch("/platform/adoption_analytics");
  if (error) return <LoadError error={error} retry={retry} label="Adoption analytics" />;
  if (!data) return <Loading />;
  const v = data.verdict || {};
  const max = Math.max(1, ...data.fleet_features.map((f) => f.events_30d));

  return (
    <div className="space-y-4" data-testid="adoption-analytics">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="fop-surface-2 rounded-lg p-3 border border-emerald-500/30 bg-emerald-500/5" data-testid="adopt-stat-succeeding">
          <div className="font-mono text-[9px] uppercase tracking-[0.18em] text-[var(--fop-text-3)]">Succeeding</div>
          <div className="font-display text-2xl text-emerald-300 mt-0.5">{v.succeeding || 0}</div>
          <div className="text-[10px] font-mono text-[var(--fop-text-4)]">≥5 feature families · 30d</div>
        </div>
        <div className="fop-surface-2 rounded-lg p-3 border border-amber-500/30 bg-amber-500/5" data-testid="adopt-stat-struggling">
          <div className="font-mono text-[9px] uppercase tracking-[0.18em] text-[var(--fop-text-3)]">Struggling</div>
          <div className="font-display text-2xl text-amber-300 mt-0.5">{v.struggling || 0}</div>
          <div className="text-[10px] font-mono text-[var(--fop-text-4)]">1-4 families · low engagement</div>
        </div>
        <div className="fop-surface-2 rounded-lg p-3 border border-red-500/30 bg-red-500/5" data-testid="adopt-stat-silent">
          <div className="font-mono text-[9px] uppercase tracking-[0.18em] text-[var(--fop-text-3)]">Silent</div>
          <div className="font-display text-2xl text-red-300 mt-0.5">{v.silent || 0}</div>
          <div className="text-[10px] font-mono text-[var(--fop-text-4)]">0 family activity · churn risk</div>
        </div>
        <div className="fop-surface-2 rounded-lg p-3 border border-[var(--fop-border)]" data-testid="adopt-stat-window">
          <div className="font-mono text-[9px] uppercase tracking-[0.18em] text-[var(--fop-text-3)]">Window</div>
          <div className="font-display text-2xl text-[var(--fop-text)] mt-0.5">30d</div>
        </div>
      </div>

      <div className="fop-surface-2 rounded-lg overflow-hidden p-3" data-testid="adoption-features">
        <SectionHeader icon={<BarChart3 size={11} />} title="Feature usage · fleet-wide · 30 days" />
        {data.fleet_features.length === 0 ? <Empty>No audit events in window.</Empty> : (
          <div className="space-y-1.5">
            {data.fleet_features.map((f) => (
              <div key={f.family} className="flex items-center gap-3 text-xs" data-testid={`feature-bar-${f.family}`}>
                <div className="w-32 font-mono uppercase text-[10px] tracking-wider text-[var(--fop-text-3)]">{f.family}</div>
                <div className="flex-1 h-2 bg-[var(--fop-surface)] rounded-full overflow-hidden">
                  <div className={`h-full ${f.events_30d ? "bg-[var(--fop-primary)]" : "bg-zinc-700"}`} style={{ width: `${Math.max(2, (f.events_30d / max) * 100)}%` }} />
                </div>
                <div className="w-16 text-right font-mono text-[11px] text-[var(--fop-text-2)]">{f.events_30d}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="fop-surface-2 rounded-lg overflow-hidden" data-testid="adoption-tenants">
        <SectionHeader icon={<TrendingUp size={11} />} title="Per-tenant engagement (top 25)" />
        {data.tenants.length === 0 ? <Empty>No tenant activity in window.</Empty> : (
          <table className="w-full text-xs">
            <thead className="bg-[var(--fop-surface)] text-[var(--fop-text-3)]">
              <tr><th className="px-3 py-2 text-left text-[10px] uppercase tracking-wider font-mono">Tenant</th><th className="px-3 py-2 text-left text-[10px] uppercase tracking-wider font-mono">Events 30d</th><th className="px-3 py-2 text-left text-[10px] uppercase tracking-wider font-mono">Families</th><th className="px-3 py-2 text-left text-[10px] uppercase tracking-wider font-mono">Used</th></tr>
            </thead>
            <tbody>
              {data.tenants.slice(0, 25).map((t) => (
                <tr key={t.tenant_id} className="border-t border-[var(--fop-border)]" data-testid={`adoption-tenant-${t.tenant_id}`}>
                  <td className="px-3 py-2"><Link to={`/platform/tenants/${t.tenant_id}`} className="hover:text-[var(--fop-primary)]">{t.tenant_name}</Link></td>
                  <td className="px-3 py-2 font-mono text-[11px] text-[var(--fop-text-2)]">{t.events_30d}</td>
                  <td className="px-3 py-2 font-mono text-[11px] text-[var(--fop-text-2)]">{t.families_count}</td>
                  <td className="px-3 py-2 text-[10px] font-mono text-[var(--fop-text-4)] truncate max-w-md">{t.families_used.join(" · ") || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
